import { tryEnemyName } from '../../combat/enemies';
import type { CharacterId } from '../../core/character';
import type { Tile } from '../../core/tile';
import { isPassable } from '../../movement/Movement';
import { showsRealFace } from '../../render/pixi/faceColour';
import { isSlainEncounter } from '../../render/tileGlyphs';

export interface HoverContent {
  /**
   * Biome-aware tile type label, or `null` while the tile is still a face-down
   * `?` frontier. A lake-biome impassable cell is typed `mountain` by worldgen
   * (HEX-130) but reads and renders as water — the label tracks the glyph and
   * says `lake`, not `mountain`, so the panel never contradicts the tile.
   */
  readonly typeLabel: string | null;
  /** Biome label — a world property, not gated on reveal. Painted as context
   * only when it adds something the type label does not (see `showBiomeContext`). */
  readonly biomeLabel: string;
  /** True for terrain that blocks entry (mountain / lake) — folded into the type
   * line as a `(impassable)` qualifier rather than a standalone noun. */
  readonly impassable: boolean;
  readonly discoveredBy: string;
  readonly lastTravelled: string;
  /**
   * Fallen-travellers line (HEX-71): `SLAIN BY <enemy>: <names>` when the tile
   * is an encounter we can name, else `FELL HERE: <names>`; `null` when nobody
   * has fallen here. Names resolve via `resolveName`, falling back per-name to
   * `a fallen traveller` when unresolvable (dead characters until the
   * History/archive ticket retains their rows).
   */
  readonly fallen: string | null;
  /**
   * Slain-encounter line (HEX-71): `<enemy> slain here` once this encounter's
   * enemy has been defeated (the tile also paints the death-mark glyph); `null`
   * otherwise.
   */
  readonly slain: string | null;
}

/**
 * Lookups the hover content builder needs to turn ids into human-readable
 * names. All optional with graceful defaults so unit tests can drive the
 * builder without a roster or the enemy registry, and so the panel never
 * crashes on an unresolvable id (the ticket's "graceful fallback").
 */
export interface HoverContentDeps {
  /** CharacterId → display name, or `null` when the character is unknown. */
  readonly resolveName?: (id: CharacterId) => string | null;
  /** Enemy template id → display name; defaults to the enemy registry. */
  readonly resolveEnemyName?: (enemyId: string) => string | null;
  readonly formatTimestamp?: (ms: number) => string;
}

const DASH = '—';
const FALLEN_FALLBACK = 'a fallen traveller';

/**
 * Pure content builder for the hover panel. Decoupled from DOM so it can be
 * unit-tested without a browser environment. The DOM-side `HoverPanel` calls
 * this on every show.
 *
 * The type label is gated on `showsRealFace` (HEX-66), not on `discoveredBy`:
 * always-visible terrain — mountains especially — shows its real face even
 * before discovery, so the panel must label it. Mountains can *never* be
 * discovered (you cannot cross them), so the old `discoveredBy` gate left the
 * panel showing only the biome ("FOREST") with no type. Frontier `?` content
 * tiles still read `null` (undiscovered identity must not leak).
 *
 * `impassable` is true for blocking terrain whose real face is shown — the DOM
 * folds it into the type line as "(impassable)" (see `hoverTypeLine`) so a wall
 * reads as one thing, not the contradictory "MOUNTAIN / IMPASSABLE / LAKE" stack
 * a lake cell used to produce (HEX-134). DISCOVERED BY / LAST TRAVELLED show the
 * metadata value or `—` when null.
 */
export function buildHoverContent(tile: Tile, deps: HoverContentDeps = {}): HoverContent {
  const resolveName = deps.resolveName ?? (() => null);
  const resolveEnemyName = deps.resolveEnemyName ?? tryEnemyName;
  const formatTimestamp = deps.formatTimestamp ?? defaultFormatTimestamp;
  const showType = showsRealFace(tile);
  const meta = tile.metadata;

  return {
    typeLabel: showType ? effectiveTypeLabel(tile) : null,
    biomeLabel: tile.biome,
    impassable: showType && !isPassable(tile.type),
    discoveredBy: resolveCharacter(meta.discoveredBy, resolveName),
    lastTravelled: buildLastTravelled(tile, resolveName, formatTimestamp),
    fallen: buildFallen(tile, resolveName, resolveEnemyName),
    slain: buildSlain(tile, resolveEnemyName),
  };
}

/**
 * Effective type label, biome-aware. Mirrors `tileGlyphFor`'s HEX-130 override:
 * a lake-biome impassable cell is typed `mountain` by worldgen yet renders as
 * water, so the label must read `lake` to agree with the glyph. Every other type
 * labels by its own name.
 */
function effectiveTypeLabel(tile: Tile): string {
  if (tile.type === 'mountain' && tile.biome === 'lake') return 'lake';
  return tile.type;
}

/**
 * The single identity line painted at the top of the panel: the type label with
 * passability folded in as a qualifier — `lake (impassable)` — so type and
 * passability never read as two separate nouns. `null` for a frontier `?` tile.
 */
export function hoverTypeLine(
  content: Pick<HoverContent, 'typeLabel' | 'impassable'>,
): string | null {
  if (content.typeLabel === null) return null;
  return content.impassable ? `${content.typeLabel} (impassable)` : content.typeLabel;
}

/**
 * Whether to paint the biome as a context line beneath the type. Suppressed when
 * it would merely echo the (biome-aware) type label — a lake cell already reads
 * `lake`, so a second `LAKE` line is noise.
 */
export function showBiomeContext(content: Pick<HoverContent, 'typeLabel' | 'biomeLabel'>): boolean {
  return content.biomeLabel !== content.typeLabel;
}

/** Resolve a character id to a name, falling back to the raw id (graceful —
 * a stable handle is better than nothing) and then to `—` when there is no id
 * at all. */
function resolveCharacter(
  id: CharacterId | null,
  resolveName: (id: CharacterId) => string | null,
): string {
  if (id === null) return DASH;
  return resolveName(id) ?? id;
}

function buildLastTravelled(
  tile: Tile,
  resolveName: (id: CharacterId) => string | null,
  formatTimestamp: (ms: number) => string,
): string {
  const { lastTraveller, lastTravelledAt } = tile.metadata;
  const when = lastTravelledAt !== null ? formatTimestamp(lastTravelledAt) : null;
  if (lastTraveller !== null) {
    const who = resolveName(lastTraveller) ?? lastTraveller;
    return when !== null ? `${who} · ${when}` : who;
  }
  // No traveller id recorded — fall back to the timestamp alone (legacy rows).
  return when ?? DASH;
}

function buildFallen(
  tile: Tile,
  resolveName: (id: CharacterId) => string | null,
  resolveEnemyName: (enemyId: string) => string | null,
): string | null {
  const fallen = tile.metadata.fallenCharacters;
  if (fallen.length === 0) return null;
  const names = fallen.map((id) => resolveName(id) ?? FALLEN_FALLBACK).join(', ');
  const enemyId = encounterEnemyId(tile);
  const enemy = enemyId !== null ? resolveEnemyName(enemyId) : null;
  return enemy !== null ? `SLAIN BY ${enemy}: ${names}` : `FELL HERE: ${names}`;
}

function buildSlain(
  tile: Tile,
  resolveEnemyName: (enemyId: string) => string | null,
): string | null {
  if (!isSlainEncounter(tile)) return null;
  const enemyId = encounterEnemyId(tile);
  const enemy = (enemyId !== null ? resolveEnemyName(enemyId) : null) ?? 'an encounter';
  return `${enemy} slain here`;
}

/** Enemy template id of an encounter tile, or `null` for any other tile. */
function encounterEnemyId(tile: Tile): string | null {
  const state = tile.lifecycleState;
  return state !== null && state.kind === 'encounter' ? state.enemyId : null;
}

function defaultFormatTimestamp(ms: number): string {
  return new Date(ms).toISOString();
}

/**
 * Plain HTML overlay that renders the hover panel on top of the renderer's
 * canvas. Not Pixi-drawn (HEX-10 spec). Caller is responsible for deciding
 * *when* to show it — i.e. only for in-vision tiles that are not in fog.
 */
export class HoverPanel {
  private root: HTMLDivElement | null = null;
  private typeEl: HTMLDivElement | null = null;
  private biomeEl: HTMLDivElement | null = null;
  private discoveredEl: HTMLSpanElement | null = null;
  private travelledEl: HTMLSpanElement | null = null;
  private fallenEl: HTMLDivElement | null = null;
  private slainEl: HTMLDivElement | null = null;

  constructor(
    private readonly container: HTMLElement,
    private readonly deps: HoverContentDeps = {},
  ) {}

  mount(): void {
    if (this.root) return;
    const root = document.createElement('div');
    root.className = 'hex6-hover-panel';
    root.setAttribute('role', 'tooltip');
    root.hidden = true;

    const typeEl = document.createElement('div');
    typeEl.className = 'hex6-hover-type';

    const biomeEl = document.createElement('div');
    biomeEl.className = 'hex6-hover-biome';

    const meta = document.createElement('dl');
    meta.className = 'hex6-hover-meta';

    const dtDiscovered = document.createElement('dt');
    dtDiscovered.textContent = 'DISCOVERED BY';
    const ddDiscovered = document.createElement('dd');
    const discoveredEl = document.createElement('span');
    ddDiscovered.appendChild(discoveredEl);

    const dtTravelled = document.createElement('dt');
    dtTravelled.textContent = 'LAST TRAVELLED';
    const ddTravelled = document.createElement('dd');
    const travelledEl = document.createElement('span');
    ddTravelled.appendChild(travelledEl);

    meta.append(dtDiscovered, ddDiscovered, dtTravelled, ddTravelled);

    const slainEl = document.createElement('div');
    slainEl.className = 'hex6-hover-slain';
    slainEl.hidden = true;

    const fallenEl = document.createElement('div');
    fallenEl.className = 'hex6-hover-fallen';
    fallenEl.hidden = true;

    root.append(typeEl, biomeEl, meta, slainEl, fallenEl);
    this.container.appendChild(root);

    this.root = root;
    this.typeEl = typeEl;
    this.biomeEl = biomeEl;
    this.discoveredEl = discoveredEl;
    this.travelledEl = travelledEl;
    this.slainEl = slainEl;
    this.fallenEl = fallenEl;
  }

  showFor(tile: Tile, screenX: number, screenY: number): void {
    if (!this.root) return;
    const content = buildHoverContent(tile, this.deps);
    if (this.typeEl) {
      const line = hoverTypeLine(content);
      this.typeEl.replaceChildren();
      if (line !== null && content.typeLabel !== null) {
        this.typeEl.append(document.createTextNode(content.typeLabel));
        // Passability rides the type line as a tinted "(impassable)" qualifier
        // so a wall reads as one thing, not a contradictory stack (HEX-134).
        if (content.impassable) {
          const qualifier = document.createElement('span');
          qualifier.className = 'hex6-hover-impassable';
          qualifier.textContent = ' (impassable)';
          this.typeEl.append(qualifier);
        }
      }
      this.typeEl.hidden = line === null;
    }
    if (this.biomeEl) {
      this.biomeEl.textContent = content.biomeLabel;
      this.biomeEl.hidden = !showBiomeContext(content);
    }
    if (this.discoveredEl) this.discoveredEl.textContent = content.discoveredBy;
    if (this.travelledEl) this.travelledEl.textContent = content.lastTravelled;
    if (this.slainEl) {
      this.slainEl.textContent = content.slain ?? '';
      this.slainEl.hidden = content.slain === null;
    }
    if (this.fallenEl) {
      this.fallenEl.textContent = content.fallen ?? '';
      this.fallenEl.hidden = content.fallen === null;
    }
    this.root.style.left = `${screenX}px`;
    this.root.style.top = `${screenY}px`;
    this.root.hidden = false;
  }

  hide(): void {
    if (!this.root) return;
    this.root.hidden = true;
  }

  isVisible(): boolean {
    return this.root ? !this.root.hidden : false;
  }

  destroy(): void {
    this.root?.remove();
    this.root = null;
    this.typeEl = null;
    this.biomeEl = null;
    this.discoveredEl = null;
    this.travelledEl = null;
    this.slainEl = null;
    this.fallenEl = null;
  }
}
